import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import countryJs from "country-list-js";
import { api } from "../utils/api";
import { Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import type z from "zod";

import { getRegisterCustomErrorMessage } from "../utils/errorMessages";
import { schemas } from "../api/generated";
import { AuthorizationErrorMessage } from "../authorization/AuthorizationErrorMessage";
import { useAuthorization } from "../authorization/useAuthorization";
import {
  defaultPasswordRules,
  PasswordRequirementsFeedback,
  usePasswordValidation,
} from "../components/PasswordRequirementsFeedback";

// Validation regexes
const usernameSchema = schemas.RegisterUserRequestBody.shape.username;
const emailSchema = schemas.RegisterUserRequestBody.shape.email;

type CreateUserData = z.infer<typeof schemas.RegisterUserRequestBody>;

export function CreateUserPage() {
  const auth = useAuthorization({ userTypes: ["Admin"] });
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<CreateUserData>({ mode: "onBlur" });
  const validation = usePasswordValidation(
    watch("password") ?? "",
    defaultPasswordRules
  );

  const onSubmit = async (data: CreateUserData) => {
    if (!auth.authorized) return;

    try {
      await api.registerUser(auth.token, data);
      toast.success("User created successfully");
      navigate("/admin-home/users");
    } catch (error) {
      toast.error(
        getRegisterCustomErrorMessage(
          error as z.infer<typeof schemas.ProblemDetails>
        )
      );
    }
  };

  if (!auth.authorized) {
    return <AuthorizationErrorMessage {...auth} />;
  }

  return (
    <div className="App container my-md-3 my-1">
      <Form
        className="register-form col-xl-4 offset-xl-4 col-lg-6 offset-lg-3 col-md-8 offset-md-2 col-12 d-flex flex-column gap-3"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <h1 className="text-center">Create User</h1>
        <Form.Group controlId="firstname">
          <Form.Label>
            First name <span>*</span>
          </Form.Label>
          <Form.Control
            {...register("firstname", { required: "First name is required" })}
            placeholder="Enter your first name"
            isInvalid={!!errors.firstname}
            autoComplete="given-name"
          />
          <Form.Control.Feedback type="invalid" role="alert">
            {errors.firstname?.message as string}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="middlename">
          <Form.Label>Middle name</Form.Label>
          <Form.Control
            {...register("middlename")}
            placeholder="Enter your middle name"
            autoComplete="middle-name"
          />
        </Form.Group>
        <Form.Group controlId="lastname">
          <Form.Label>
            Last name <span>*</span>
          </Form.Label>
          <Form.Control
            {...register("lastname", { required: "Last name is required" })}
            placeholder="Enter your last name"
            isInvalid={!!errors.lastname}
            autoComplete="family-name"
          />
          <Form.Control.Feedback type="invalid" role="alert">
            {errors.lastname?.message as string}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="username">
          <Form.Label>
            Username <span>*</span>
          </Form.Label>
          <Form.Control
            {...register("username", {
              required: "Username is required",
              minLength: {
                value: 3,
                message: "Username must be at least 3 characters long.",
              },
              validate: (value) =>
                usernameSchema.safeParse(value).success
                  ? true
                  : "Username must only contain letters, digits, '.', '-' or '_'.",
            })}
            type="text"
            maxLength={50}
            placeholder="Enter a unique username"
            isInvalid={!!errors.username}
            autoComplete="username"
          />
          <Form.Control.Feedback type="invalid" role="alert">
            {errors.username?.message as string}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="email">
          <Form.Label>
            Email <span>*</span>
          </Form.Label>
          <Form.Control
            {...register("email", {
              required: "Email is required",
              validate: (value) =>
                emailSchema.safeParse(value).success
                  ? true
                  : "Email must be a valid email address.",
            })}
            type="email"
            maxLength={254}
            placeholder="Enter your email address"
            isInvalid={!!errors.email}
            autoComplete="email"
          />
          <Form.Control.Feedback type="invalid" role="alert">
            {errors.email?.message as string}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="country">
          <Form.Label>
            Country <span>*</span>
          </Form.Label>
          <Form.Select
            {...register("country", { required: "Country is required" })}
            required
            isInvalid={!!errors.country}
            autoComplete="country"
          >
            <option value="">Select a country</option>
            {countryJs
              .names()
              .sort()
              .map((el) => (
                <option key={el} value={el}>
                  {el}
                </option>
              ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid" role="alert">
            {errors.country?.message as string}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group controlId="password">
          <Form.Label>
            Password <span>*</span>
          </Form.Label>
          <Form.Control
            {...register("password", {
              required: "Password is required",
              onBlur: (e) => {
                e.target.value = e.target.value.trim();
              },
              validate: () =>
                validation.isValid || "Password does not meet requirements.",
            })}
            maxLength={512}
            type="password"
            placeholder="Enter a strong password"
            isInvalid={!!errors.password}
            autoComplete="new-password"
            aria-describedby="passwordFeedback"
            className="pl-10 small"
          />
          <Form.Control.Feedback type="invalid" role="alert">
            {errors.password?.message as string}
          </Form.Control.Feedback>
          <PasswordRequirementsFeedback
            id="passwordFeedback"
            {...validation}
            className="ms-1 mt-1 small border-start ps-3"
          />
        </Form.Group>
        <Button
          variant="dark"
          type="submit"
          className="mt-3 align-self-start"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create User"}
        </Button>
      </Form>
    </div>
  );
}
